// import React, { useState, useMemo } from "react";
// import moment from "moment";
// import { Download } from "lucide-react";
// import Layout from "../../components/layout/Layout";
// import { Button } from "../../components/ui/button";
// import MainHeading from "../../components/common/MainHeading";
// import CommonFilter, { type IFilter } from "../../components/common/CommonFilter";
// import CommonTable, { type TableColumn } from "../../components/common/CommonTable";
// import TablePagination from "../../components/common/TablePagination";
// import type { IPageInfo } from "@/interface/common.interface";

// // ─────────────────────────────────────────────────────────────────────────
// // Types
// // ─────────────────────────────────────────────────────────────────────────
// type AttendanceStatus = "P" | "A" | "HD" | "L";
// type Shift = "Day Shift" | "Night Shift";
// type DisplayStatus = "On-Time" | "Late" | "Absent" | "On Leave" | "Half Day";

// interface Employee {
//   id: string;
//   name: string;
//   role: string;
//   siteNo: string;
//   siteName: string;
//   shift: Shift;
//   attendance: Record<number, AttendanceStatus>;
// }

// interface PunchTimes {
//   inTime: string;
//   outTime: string;
//   lateMinutes: number;
// }

// interface AttendanceRow {
//   key: string;
//   emp: Employee;
//   idx: number;
//   date: moment.Moment;
//   punch: PunchTimes | null;
//   displayStatus: DisplayStatus;
//   totalMinutes: number | null;
//   overtimeMinutes: number | null;
// }

// // ─────────────────────────────────────────────────────────────────────────
// // Mock data
// // ─────────────────────────────────────────────────────────────────────────
// const rawEmployees: Array<{
//   name: string;
//   role: string;
//   siteNo: string;
//   siteName: string;
//   shift: Shift;
// }> = [
//   { name: "Ramesh Kumar", role: "Mason", siteNo: "Site 4", siteName: "Roofing", shift: "Day Shift" },
//   { name: "Sunita Devi", role: "Helper", siteNo: "Site 2", siteName: "Block A", shift: "Day Shift" },
//   { name: "Arvind Yadav", role: "Mason", siteNo: "Site 1", siteName: "Foundation", shift: "Night Shift" },
//   { name: "Priya Sharma", role: "Supervisor", siteNo: "Site 3", siteName: "Interior", shift: "Day Shift" },
//   { name: "Vikram Singh", role: "Helper", siteNo: "Site 4", siteName: "Roofing", shift: "Night Shift" },
//   { name: "Anita Patel", role: "Carpenter", siteNo: "Site 2", siteName: "Block A", shift: "Day Shift" },
//   { name: "Manoj Tiwari", role: "Electrician", siteNo: "Site 1", siteName: "Foundation", shift: "Night Shift" },
//   { name: "Rekha Joshi", role: "Site Accountant", siteNo: "HO", siteName: "Head Office", shift: "Day Shift" },
//   { name: "Deepak Rana", role: "Plumber", siteNo: "Site 3", siteName: "Interior", shift: "Day Shift" },
//   { name: "Kavita Nair", role: "HR Officer", siteNo: "HO", siteName: "Head Office", shift: "Night Shift" },
// ];

// const getMockAttendance = (nameIndex: number) => {
//   const statuses: AttendanceStatus[] = ["P", "P", "P", "P", "P", "A", "HD", "L"];
//   const att: Record<number, AttendanceStatus> = {};
//   for (let d = 1; d <= 31; d++) {
//     att[d] = statuses[(d + nameIndex * 3) % statuses.length];
//   }
//   return att;
// };

// const employees: Employee[] = rawEmployees.map((e, idx) => ({
//   id: `EMP-${String(idx + 234).padStart(4, "0")}`,
//   ...e,
//   attendance: getMockAttendance(idx),
// }));

// // Deterministic punch in/out for Present / Half Day
// const getPunchTimes = (
//   status: AttendanceStatus,
//   dayNum: number,
//   empIdx: number,
//   shift: Shift,
// ): PunchTimes | null => {
//   const baseIn = shift === "Day Shift" ? 9 : 21; // 9 AM or 9 PM
//   const baseOutFull = shift === "Day Shift" ? 17 : 5; // 5 PM or 5 AM
//   const baseOutHalf = shift === "Day Shift" ? 13 : 1; // 1 PM or 1 AM

//   const fmt = (hour24: number, min: number) => {
//     const period = hour24 >= 12 && hour24 < 24 ? "PM" : "AM";
//     let hour12 = hour24 % 12;
//     if (hour12 === 0) hour12 = 12;
//     return `${String(hour12).padStart(2, "0")}:${String(min).padStart(2, "0")} ${period}`;
//   };

//   if (status === "P") {
//     const inMin = (dayNum + empIdx * 7) % 20;
//     const outMin = (dayNum * empIdx * 3) % 30;
//     return { inTime: fmt(baseIn, Math.min(inMin, 14)), outTime: fmt(baseOutFull, outMin), lateMinutes: inMin };
//   }
//   if (status === "HD") {
//     const inMin = (dayNum + empIdx * 7) % 20;
//     const outMin = (dayNum * empIdx * 3) % 30;
//     return { inTime: fmt(baseIn, inMin), outTime: fmt(baseOutHalf, outMin), lateMinutes: inMin };
//   }
//   return null;
// };

// const toDisplayStatus = (status: AttendanceStatus, lateMinutes: number | null): DisplayStatus => {
//   if (status === "A") return "Absent";
//   if (status === "L") return "On Leave";
//   if (status === "HD") return "Half Day";
//   return lateMinutes !== null && lateMinutes > 9 ? "Late" : "On-Time";
// };

// const statusStyles: Record<DisplayStatus, string> = {
//   "On-Time": "bg-emerald-600 text-white",
//   Late: "bg-amber-100 text-amber-800",
//   Absent: "bg-slate-900 text-white",
//   "On Leave": "bg-slate-700 text-white",
//   "Half Day": "bg-sky-100 text-sky-800",
// };

// const shiftDot: Record<Shift, string> = {
//   "Day Shift": "bg-amber-400",
//   "Night Shift": "bg-indigo-600",
// };

// const avatarPalette = [
//   "bg-rose-100 text-rose-700",
//   "bg-indigo-100 text-indigo-700",
//   "bg-amber-100 text-amber-800",
//   "bg-emerald-100 text-emerald-700",
//   "bg-sky-100 text-sky-700",
//   "bg-violet-100 text-violet-700",
//   "bg-teal-100 text-teal-700",
//   "bg-orange-100 text-orange-700",
// ];

// const durationMinutes = (inTime: string, outTime: string) => {
//   const start = moment(inTime, "hh:mm A");
//   let end = moment(outTime, "hh:mm A");
//   if (end.isBefore(start)) end = end.add(1, "day"); // crosses midnight for night shift
//   return end.diff(start, "minutes");
// };

// const formatMinutes = (mins: number) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

// // ─────────────────────────────────────────────────────────────────────────
// // Filter option lists
// // ─────────────────────────────────────────────────────────────────────────
// const statusFilterOptions = [
//   { label: "All", value: "" },
//   { label: "On-Time", value: "On-Time" },
//   { label: "Late", value: "Late" },
//   { label: "Absent", value: "Absent" },
//   { label: "On Leave", value: "On Leave" },
//   { label: "Half Day", value: "Half Day" },
// ];

// const sortOptions = [
//   { label: "Name", value: "name" },
//   { label: "Date", value: "date" },
//   { label: "Status", value: "status" },
//   { label: "Duration", value: "duration" },
// ];

// // ─────────────────────────────────────────────────────────────────────────
// // Component
// // ─────────────────────────────────────────────────────────────────────────
// const AttendanceReport: React.FC = () => {
//   const [filter, setFilter] = useState<IFilter>({
//     search: "",
//     filter: "",
//     sort: "name",
//     startDate: moment().startOf("month").format("YYYY-MM-DD"),
//     endDate: moment().endOf("month").format("YYYY-MM-DD"),
//   });

//   const [paginationData, setPaginationData] = useState<IPageInfo>({
//     currentPage: 1,
//     pageSize: 10,
//     total: 0,
//     totalPages: 0,
//   });

//   // Build every employee x day record across the selected date range
//   const rows = useMemo(() => {
//     const start = moment(filter.startDate);
//     const end = moment(filter.endDate);
//     const days: moment.Moment[] = [];
//     const cursor = start.clone();
//     while (cursor.isSameOrBefore(end, "day") && days.length < 62) {
//       days.push(cursor.clone());
//       cursor.add(1, "day");
//     }

//     const records: AttendanceRow[] = [];

//     employees.forEach((emp, idx) => {
//       days.forEach((date) => {
//         const dayNum = date.date();
//         const status = emp.attendance[dayNum];
//         const punch =
//           status === "P" || status === "HD" ? getPunchTimes(status, dayNum, idx, emp.shift) : null;
//         const displayStatus = toDisplayStatus(status, punch ? punch.lateMinutes : null);
//         const totalMinutes = punch ? durationMinutes(punch.inTime, punch.outTime) : null;
//         const overtimeMinutes =
//           totalMinutes !== null ? Math.max(0, totalMinutes - (status === "HD" ? 240 : 480)) : null;
//         records.push({
//           key: `${emp.id}-${date.format("YYYY-MM-DD")}`,
//           emp,
//           idx,
//           date,
//           punch,
//           displayStatus,
//           totalMinutes,
//           overtimeMinutes,
//         });
//       });
//     });

//     return records;
//   }, [filter.startDate, filter.endDate]);

//   const filteredRows = useMemo(() => {
//     const search = (filter.search || "").toLowerCase();
//     let result = rows.filter(({ emp, displayStatus }) => {
//       const matchesSearch =
//         !search || emp.name.toLowerCase().includes(search) || emp.id.toLowerCase().includes(search);
//       const matchesStatus = !filter.filter || displayStatus === filter.filter;
//       return matchesSearch && matchesStatus;
//     });

//     const sortKey = filter.sort || "name";
//     result = [...result].sort((a, b) => {
//       if (sortKey === "name") return a.emp.name.localeCompare(b.emp.name);
//       if (sortKey === "date") return a.date.valueOf() - b.date.valueOf();
//       if (sortKey === "status") return a.displayStatus.localeCompare(b.displayStatus);
//       if (sortKey === "duration") return (b.totalMinutes ?? -1) - (a.totalMinutes ?? -1);
//       return 0;
//     });

//     return result;
//   }, [rows, filter.search, filter.filter, filter.sort]);

//   const totalPages = Math.max(1, Math.ceil(filteredRows.length / paginationData.pageSize));
//   const currentPage = Math.min(paginationData.currentPage, totalPages);
//   const pagedRows = filteredRows.slice(
//     (currentPage - 1) * paginationData.pageSize,
//     currentPage * paginationData.pageSize,
//   );

//   const tablePaginationData: IPageInfo = {
//     ...paginationData,
//     currentPage,
//     total: filteredRows.length,
//     totalPages,
//   };

//   const handleSearchChange = (value: string) => {
//     setFilter((prev) => ({ ...prev, search: value }));
//     setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
//   };

//   const handleFilterChange = (value: string, name: string) => {
//     setFilter((prev) => ({ ...prev, [name]: value }));
//     setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
//   };

//   const handleDateRangeChange = (start: string, end: string) => {
//     setFilter((prev) => ({
//       ...prev,
//       startDate: start || moment().startOf("month").format("YYYY-MM-DD"),
//       endDate: end || moment().endOf("month").format("YYYY-MM-DD"),
//     }));
//     setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
//   };

//   const handleChangePageSize = (value: number) => {
//     setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
//   };

//   const handleExportCSV = () => {
//     const headers = ["ID", "Name", "Site", "Date", "Shift", "Check In", "Check Out", "Duration", "Overtime", "Status"];
//     const csvRows = filteredRows.map(({ emp, date, punch, totalMinutes, overtimeMinutes, displayStatus }) => [
//       emp.id,
//       emp.name,
//       `${emp.siteNo} – ${emp.siteName}`,
//       date.format("DD MMM YYYY"),
//       emp.shift,
//       punch ? punch.inTime : "--",
//       punch ? punch.outTime : "--",
//       totalMinutes !== null ? formatMinutes(totalMinutes) : "--",
//       overtimeMinutes ? formatMinutes(overtimeMinutes) : "--",
//       displayStatus,
//     ]);
//     const csvContent =
//       "data:text/csv;charset=utf-8," +
//       [headers.join(","), ...csvRows.map((r) => r.map((v) => `"${v}"`).join(","))].join("\n");
//     const link = document.createElement("a");
//     link.setAttribute("href", encodeURI(csvContent));
//     link.setAttribute(
//       "download",
//       `Attendance_${moment(filter.startDate).format("DD_MMM")}_to_${moment(filter.endDate).format("DD_MMM_YYYY")}.csv`,
//     );
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   // Columns — same TableColumn<T> shape used by CommonTable across the app
//   const columns: TableColumn<AttendanceRow>[] = [
//     {
//       key: "name",
//       header: "Name",
//       render: (item) => {
//         const initials = item.emp.name
//           .split(" ")
//           .map((n) => n[0])
//           .join("")
//           .slice(0, 2)
//           .toUpperCase();
//         return (
//           <div className="flex items-center gap-3">
//             <div
//               className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${avatarPalette[item.idx % avatarPalette.length]}`}
//             >
//               {initials}
//             </div>
//             <div>
//               <p className="font-semibold text-slate-800 leading-tight">{item.emp.name}</p>
//               <p className="text-xs text-slate-400">{item.emp.id}</p>
//             </div>
//           </div>
//         );
//       },
//     },
//     {
//       key: "site",
//       header: "Site",
//       render: (item) => (
//         <div>
//           <p className="font-medium text-slate-700 leading-tight">{item.emp.siteNo}</p>
//           <p className="text-xs text-slate-400">{item.emp.siteName}</p>
//         </div>
//       ),
//     },
//     {
//       key: "date",
//       header: "Date",
//       render: (item) => <span className="text-slate-600 whitespace-nowrap">{item.date.format("DD MMM YYYY")}</span>,
//     },
//     {
//       key: "shift",
//       header: "Shift",
//       render: (item) => (
//         <span className="inline-flex items-center gap-1.5 text-slate-600 whitespace-nowrap">
//           <span className={`w-2 h-2 rounded-full ${shiftDot[item.emp.shift]}`} />
//           {item.emp.shift}
//         </span>
//       ),
//     },
//     {
//       key: "checkInOut",
//       header: "Check In - Out",
//       render: (item) => (
//         <span className="text-slate-600 whitespace-nowrap">
//           {item.punch ? `${item.punch.inTime} - ${item.punch.outTime}` : "— - —"}
//         </span>
//       ),
//     },
//     {
//       key: "duration",
//       header: "Duration",
//       render: (item) => (
//         <span className="text-slate-600 whitespace-nowrap">
//           {item.totalMinutes !== null ? formatMinutes(item.totalMinutes) : "—"}
//         </span>
//       ),
//     },
//     {
//       key: "overtime",
//       header: "Overtime",
//       render: (item) => (
//         <span className="text-slate-600 whitespace-nowrap">
//           {item.overtimeMinutes ? formatMinutes(item.overtimeMinutes) : "—"}
//         </span>
//       ),
//     },
//     {
//       key: "status",
//       header: "Status",
//       render: (item) => (
//         <span
//           className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusStyles[item.displayStatus]}`}
//         >
//           {item.displayStatus}
//         </span>
//       ),
//     },
//   ];

//   return (
//     <div className="space-y-6">
//       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//         <MainHeading
//           title="Employee Attendance"
//           actions={
//             <Button
//               onClick={handleExportCSV}
//               disabled={filteredRows.length === 0}
//               className="gap-2"
//             >
//               <Download size={16} /> Export
//             </Button>
//           }
//         />
//       </div>

//       <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
//         <CommonFilter
//           searchValue={filter.search || ""}
//           onSearchChange={handleSearchChange}
//           searchPlaceholder="Search labor name..."
//           value={filter}
//           onChange={handleFilterChange}
//           filterOptions={statusFilterOptions}
//           filterName="filter"
//           filterPlaceholder="Filter"
//           sortOptions={sortOptions}
//           sortName="sort"
//           sortPlaceholder="Sort by: Name"
//           startDate={filter.startDate}
//           endDate={filter.endDate}
//           onDateRangeChange={handleDateRangeChange}
//         />
//         <CommonTable data={pagedRows} columns={columns} loading={false} stickyColumns={2} />
//         <TablePagination
//           paginationData={tablePaginationData}
//           setPaginationData={setPaginationData}
//           handleChangePageSize={handleChangePageSize}
//           isLoading={false}
//         />
//       </div>
//     </div>
//   );
// };

// export default AttendanceReport;


import React, { useState, useMemo } from "react";
import moment from "moment";
import { Download } from "lucide-react";
import Layout from "../../components/layout/Layout";
import { Button } from "../../components/ui/button";
import MainHeading from "../../components/common/MainHeading";
import CommonFilter, { type IFilter } from "../../components/common/CommonFilter";
import CommonTable, { type TableColumn } from "../../components/common/CommonTable";
import TablePagination from "../../components/common/TablePagination";
import type { IPageInfo } from "@/interface/common.interface";
import { ShiftBadge, StatusBadge, SiteLabel, AvatarCell } from "../../components/common/Badge";

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────
type AttendanceStatus = "P" | "A" | "HD" | "L";
type Shift = "Day Shift" | "Night Shift";
type DisplayStatus = "On-Time" | "Late" | "Absent" | "On Leave" | "Half Day";

interface Employee {
  id: string;
  name: string;
  role: string;
  siteNo: string;
  siteName: string;
  shift: Shift;
  attendance: Record<number, AttendanceStatus>;
}

interface PunchTimes {
  inTime: string;
  outTime: string;
  lateMinutes: number;
}

interface AttendanceRow {
  key: string;
  emp: Employee;
  idx: number;
  date: moment.Moment;
  punch: PunchTimes | null;
  displayStatus: DisplayStatus;
  totalMinutes: number | null;
  overtimeMinutes: number | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Mock data
// ─────────────────────────────────────────────────────────────────────────
const rawEmployees: Array<{
  name: string;
  role: string;
  siteNo: string;
  siteName: string;
  shift: Shift;
}> = [
  { name: "Ramesh Kumar", role: "Mason", siteNo: "Site 4", siteName: "Roofing", shift: "Day Shift" },
  { name: "Sunita Devi", role: "Helper", siteNo: "Site 2", siteName: "Block A", shift: "Day Shift" },
  { name: "Arvind Yadav", role: "Mason", siteNo: "Site 1", siteName: "Foundation", shift: "Night Shift" },
  { name: "Priya Sharma", role: "Supervisor", siteNo: "Site 3", siteName: "Interior", shift: "Day Shift" },
  { name: "Vikram Singh", role: "Helper", siteNo: "Site 4", siteName: "Roofing", shift: "Night Shift" },
  { name: "Anita Patel", role: "Carpenter", siteNo: "Site 2", siteName: "Block A", shift: "Day Shift" },
  { name: "Manoj Tiwari", role: "Electrician", siteNo: "Site 1", siteName: "Foundation", shift: "Night Shift" },
  { name: "Rekha Joshi", role: "Site Accountant", siteNo: "HO", siteName: "Head Office", shift: "Day Shift" },
  { name: "Deepak Rana", role: "Plumber", siteNo: "Site 3", siteName: "Interior", shift: "Day Shift" },
  { name: "Kavita Nair", role: "HR Officer", siteNo: "HO", siteName: "Head Office", shift: "Night Shift" },
];

const getMockAttendance = (nameIndex: number) => {
  const statuses: AttendanceStatus[] = ["P", "P", "P", "P", "P", "A", "HD", "L"];
  const att: Record<number, AttendanceStatus> = {};
  for (let d = 1; d <= 31; d++) {
    att[d] = statuses[(d + nameIndex * 3) % statuses.length];
  }
  return att;
};

const employees: Employee[] = rawEmployees.map((e, idx) => ({
  id: `EMP-${String(idx + 234).padStart(4, "0")}`,
  ...e,
  attendance: getMockAttendance(idx),
}));

// Deterministic punch in/out for Present / Half Day
const getPunchTimes = (
  status: AttendanceStatus,
  dayNum: number,
  empIdx: number,
  shift: Shift,
): PunchTimes | null => {
  const baseIn = shift === "Day Shift" ? 9 : 21; // 9 AM or 9 PM
  const baseOutFull = shift === "Day Shift" ? 17 : 5; // 5 PM or 5 AM
  const baseOutHalf = shift === "Day Shift" ? 13 : 1; // 1 PM or 1 AM

  const fmt = (hour24: number, min: number) => {
    const period = hour24 >= 12 && hour24 < 24 ? "PM" : "AM";
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    return `${String(hour12).padStart(2, "0")}:${String(min).padStart(2, "0")} ${period}`;
  };

  if (status === "P") {
    const inMin = (dayNum + empIdx * 7) % 20;
    const outMin = (dayNum * empIdx * 3) % 30;
    return { inTime: fmt(baseIn, Math.min(inMin, 14)), outTime: fmt(baseOutFull, outMin), lateMinutes: inMin };
  }
  if (status === "HD") {
    const inMin = (dayNum + empIdx * 7) % 20;
    const outMin = (dayNum * empIdx * 3) % 30;
    return { inTime: fmt(baseIn, inMin), outTime: fmt(baseOutHalf, outMin), lateMinutes: inMin };
  }
  return null;
};

const toDisplayStatus = (status: AttendanceStatus, lateMinutes: number | null): DisplayStatus => {
  if (status === "A") return "Absent";
  if (status === "L") return "On Leave";
  if (status === "HD") return "Half Day";
  return lateMinutes !== null && lateMinutes > 9 ? "Late" : "On-Time";
};

const durationMinutes = (inTime: string, outTime: string) => {
  const start = moment(inTime, "hh:mm A");
  let end = moment(outTime, "hh:mm A");
  if (end.isBefore(start)) end = end.add(1, "day"); // crosses midnight for night shift
  return end.diff(start, "minutes");
};

const formatMinutes = (mins: number) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

// ─────────────────────────────────────────────────────────────────────────
// Filter option lists
// ─────────────────────────────────────────────────────────────────────────
const statusFilterOptions = [
  { label: "All", value: "" },
  { label: "On-Time", value: "On-Time" },
  { label: "Late", value: "Late" },
  { label: "Absent", value: "Absent" },
  { label: "On Leave", value: "On Leave" },
  { label: "Half Day", value: "Half Day" },
];

const sortOptions = [
  { label: "Name", value: "name" },
  { label: "Date", value: "date" },
  { label: "Status", value: "status" },
  { label: "Duration", value: "duration" },
];

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────
const AttendanceReport: React.FC = () => {
  const [filter, setFilter] = useState<IFilter>({
    search: "",
    filter: "",
    sort: "name",
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // Build every employee x day record across the selected date range
  const rows = useMemo(() => {
    const start = moment(filter.startDate);
    const end = moment(filter.endDate);
    const days: moment.Moment[] = [];
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, "day") && days.length < 62) {
      days.push(cursor.clone());
      cursor.add(1, "day");
    }

    const records: AttendanceRow[] = [];

    employees.forEach((emp, idx) => {
      days.forEach((date) => {
        const dayNum = date.date();
        const status = emp.attendance[dayNum];
        const punch =
          status === "P" || status === "HD" ? getPunchTimes(status, dayNum, idx, emp.shift) : null;
        const displayStatus = toDisplayStatus(status, punch ? punch.lateMinutes : null);
        const totalMinutes = punch ? durationMinutes(punch.inTime, punch.outTime) : null;
        const overtimeMinutes =
          totalMinutes !== null ? Math.max(0, totalMinutes - (status === "HD" ? 240 : 480)) : null;
        records.push({
          key: `${emp.id}-${date.format("YYYY-MM-DD")}`,
          emp,
          idx,
          date,
          punch,
          displayStatus,
          totalMinutes,
          overtimeMinutes,
        });
      });
    });

    return records;
  }, [filter.startDate, filter.endDate]);

  const filteredRows = useMemo(() => {
    const search = (filter.search || "").toLowerCase();
    let result = rows.filter(({ emp, displayStatus }) => {
      const matchesSearch =
        !search || emp.name.toLowerCase().includes(search) || emp.id.toLowerCase().includes(search);
      const matchesStatus = !filter.filter || displayStatus === filter.filter;
      return matchesSearch && matchesStatus;
    });

    const sortKey = filter.sort || "name";
    result = [...result].sort((a, b) => {
      if (sortKey === "name") return a.emp.name.localeCompare(b.emp.name);
      if (sortKey === "date") return a.date.valueOf() - b.date.valueOf();
      if (sortKey === "status") return a.displayStatus.localeCompare(b.displayStatus);
      if (sortKey === "duration") return (b.totalMinutes ?? -1) - (a.totalMinutes ?? -1);
      return 0;
    });

    return result;
  }, [rows, filter.search, filter.filter, filter.sort]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / paginationData.pageSize));
  const currentPage = Math.min(paginationData.currentPage, totalPages);
  const pagedRows = filteredRows.slice(
    (currentPage - 1) * paginationData.pageSize,
    currentPage * paginationData.pageSize,
  );

  const tablePaginationData: IPageInfo = {
    ...paginationData,
    currentPage,
    total: filteredRows.length,
    totalPages,
  };

  const handleSearchChange = (value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleFilterChange = (value: string, name: string) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleDateRangeChange = (start: string, end: string) => {
    setFilter((prev) => ({
      ...prev,
      startDate: start || moment().startOf("month").format("YYYY-MM-DD"),
      endDate: end || moment().endOf("month").format("YYYY-MM-DD"),
    }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const handleExportCSV = () => {
    const headers = ["ID", "Name", "Site", "Date", "Shift", "Check In", "Check Out", "Duration", "Overtime", "Status"];
    const csvRows = filteredRows.map(({ emp, date, punch, totalMinutes, overtimeMinutes, displayStatus }) => [
      emp.id,
      emp.name,
      `${emp.siteNo} – ${emp.siteName}`,
      date.format("DD MMM YYYY"),
      emp.shift,
      punch ? punch.inTime : "--",
      punch ? punch.outTime : "--",
      totalMinutes !== null ? formatMinutes(totalMinutes) : "--",
      overtimeMinutes ? formatMinutes(overtimeMinutes) : "--",
      displayStatus,
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...csvRows.map((r) => r.map((v) => `"${v}"`).join(","))].join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute(
      "download",
      `Attendance_${moment(filter.startDate).format("DD_MMM")}_to_${moment(filter.endDate).format("DD_MMM_YYYY")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Columns — Shift / Status / Site now share the same Badge component
  // as every other table in the app.
  const columns: TableColumn<AttendanceRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (item) => <AvatarCell name={item.emp.name} subLabel={item.emp.id} paletteIndex={item.idx} />,
    },
    {
      key: "site",
      header: "Site",
      render: (item) => <SiteLabel label={item.emp.siteNo} sub={item.emp.siteName} />,
    },
    {
      key: "date",
      header: "Date",
      render: (item) => <span className="text-slate-600 whitespace-nowrap">{item.date.format("DD MMM YYYY")}</span>,
    },
    {
      key: "shift",
      header: "Shift",
      render: (item) => <ShiftBadge shift={item.emp.shift} />,
    },
    {
      key: "checkInOut",
      header: "Check In - Out",
      render: (item) => (
        <span className="text-slate-600 whitespace-nowrap">
          {item.punch ? `${item.punch.inTime} - ${item.punch.outTime}` : "— - —"}
        </span>
      ),
    },
    {
      key: "duration",
      header: "Duration",
      render: (item) => (
        <span className="text-slate-600 whitespace-nowrap">
          {item.totalMinutes !== null ? formatMinutes(item.totalMinutes) : "—"}
        </span>
      ),
    },
    {
      key: "overtime",
      header: "Overtime",
      render: (item) => (
        <span className="text-slate-600 whitespace-nowrap">
          {item.overtimeMinutes ? formatMinutes(item.overtimeMinutes) : "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => <StatusBadge status={item.displayStatus} />,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <MainHeading
          title="Employee Attendance"
          actions={
            <Button onClick={handleExportCSV} disabled={filteredRows.length === 0} className="gap-2">
              <Download size={16} /> Export
            </Button>
          }
        />
      </div>

      <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
        <CommonFilter
          searchValue={filter.search || ""}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search labor name..."
          value={filter}
          onChange={handleFilterChange}
          filterOptions={statusFilterOptions}
          filterName="filter"
          filterPlaceholder="Filter"
          sortOptions={sortOptions}
          sortName="sort"
          sortPlaceholder="Sort by: Name"
          startDate={filter.startDate}
          endDate={filter.endDate}
          onDateRangeChange={handleDateRangeChange}
        />
        <CommonTable data={pagedRows} columns={columns} loading={false} stickyColumns={2} />
        <TablePagination
          paginationData={tablePaginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={false}
        />
      </div>
    </div>
  );
};

export default AttendanceReport;